import { ChecklistEvaluationKey, computeSignedToleranceVerdict } from './ChecklistEvaluations';
import type { EvaluationCoreDescriptor, ToleranceBound } from './ChecklistEvaluations';
import { FlagPurposeType } from './Flags';

const DEFAULT_BOUND: ToleranceBound = { minPct: -0.05, maxPct: 0.1 };

const formatPct = (pct: number): string => `${(pct * 100).toFixed(1)}%`;

const formatBound = (bound: ToleranceBound): string => {
  const min = bound.minPct !== undefined ? formatPct(bound.minPct) : undefined;
  const max = bound.maxPct !== undefined ? `+${formatPct(bound.maxPct)}` : undefined;
  if (min !== undefined && max !== undefined) return `${min}/${max}`;
  return min ?? max ?? 'no bound';
};

const round = (value: number, decimals = 2): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export const systemSizeCore: EvaluationCoreDescriptor = {
  key: ChecklistEvaluationKey.systemSize,
  label: 'System Size',
  repeatable: false,
  supportsDecisionOptions: true,
  flagPurposeType: FlagPurposeType.changeOrder,
  defaultParams: {
    tolerancesByFinanceType: {
      ppa: { minPct: -0.25, maxPct: 0.25 },
      lease: { minPct: -0.05, maxPct: 0.1 },
    },
  },
  // Ordered as the UI renders them: both derived reference values, then the reviewer's entry.
  // `asBuiltSystemSizeKw` is derived-only (never read by computeRow) but still renders as a metric
  // row, so it needs an entry here — its literal name collides with a ChecklistReferenceKey.
  fieldFormats: {
    contractSystemSizeKw: { label: 'Contract', unit: 'kW', decimals: 2 },
    asBuiltSystemSizeKw: { label: 'Production Report', unit: 'kW', decimals: 2 },
    asBuiltSizeKw: { label: 'As-Built System Size', unit: 'kW', decimals: 2 },
  },
  computeRow: (derived, entered, params) => {
    const base = derived.contractSystemSizeKw;
    const actual = entered.asBuiltSizeKw;
    const bound = params.tolerancesByFinanceType?.default ?? DEFAULT_BOUND;
    return computeSignedToleranceVerdict(base, actual, bound);
  },
  buildFlagReason: (rows, params) => {
    const row = rows[0];
    const base = row?.derived.contractSystemSizeKw;
    const actual = row?.entered.asBuiltSizeKw;
    const result = row?.result;
    const bound = params.tolerancesByFinanceType?.default ?? DEFAULT_BOUND;

    if (base === undefined || actual === null || actual === undefined || result?.percentDifference === undefined) {
      return 'As-built system size is outside the allowed tolerance of contract system size.';
    }

    const direction = result.percentDifference >= 0 ? 'above' : 'below';
    const pctText = `${formatPct(Math.abs(result.percentDifference))} ${direction}`;

    return `As-built system size ${round(actual)} kW is ${pctText} contract ${round(base)} kW (allowed ${formatBound(bound)}).`;
  },
};

export const firstYearProductionCore: EvaluationCoreDescriptor = {
  key: ChecklistEvaluationKey.firstYearProduction,
  label: 'First-Year Production',
  repeatable: false,
  supportsDecisionOptions: true,
  flagPurposeType: FlagPurposeType.changeOrder,
  defaultParams: {
    tolerancesByFinanceType: {
      default: { minPct: -0.05, maxPct: 0.1 },
    },
  },
  // `productionReportKwh` is derived-only (never read by computeRow) but still renders as a metric
  // row, so it needs an entry here to keep its label and thousands separator.
  fieldFormats: {
    contractFirstYearProductionKwh: { label: 'Contract', unit: 'kWh', decimals: 0 },
    productionReportKwh: { label: 'Production Report', unit: 'kWh', decimals: 0 },
    productionModelKwh: { label: 'Production Model Value', unit: 'kWh', decimals: 0 },
  },
  computeRow: (derived, entered, params) => {
    const base = derived.contractFirstYearProductionKwh;
    const actual = entered.productionModelKwh;
    const bound = params.tolerancesByFinanceType?.default ?? DEFAULT_BOUND;
    return computeSignedToleranceVerdict(base, actual, bound);
  },
  buildFlagReason: (rows, params) => {
    const row = rows[0];
    const base = row?.derived.contractFirstYearProductionKwh;
    const actual = row?.entered.productionModelKwh;
    const result = row?.result;
    const bound = params.tolerancesByFinanceType?.default ?? DEFAULT_BOUND;

    if (base === undefined || actual === null || actual === undefined || result?.percentDifference === undefined) {
      return 'Production model value is outside the allowed tolerance of contract first-year production.';
    }

    const direction = result.percentDifference >= 0 ? 'above' : 'below';
    const pctText = `${formatPct(Math.abs(result.percentDifference))} ${direction}`;

    return `Production model value ${round(actual)} kWh is ${pctText} contract ${round(base)} kWh (allowed ${formatBound(bound)}).`;
  },
};
